import './lib/env';
import '@total-typescript/ts-reset';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { routeTree } from './routeTree.gen';
import { AppErrorBoundary } from './components/providers/app-error-boundary';
import { setUnauthorizedHandler } from './lib/api/client';
import { shouldRetry, calcRetryDelay } from './lib/api/retry';
import { reportError } from './lib/observability';
import './index.css';

// 新版本部署后,旧标签页会按旧 hash 拉取已被替换的懒加载 chunk,
// 触发 "Failed to fetch dynamically imported module"。刷新一次拿到
// 新 index.html 即可恢复;sessionStorage 时间戳防止刷新循环。
window.addEventListener('vite:preloadError', (event) => {
  const KEY = 'chunk-reload-at';
  const last = Number(sessionStorage.getItem(KEY) ?? 0);
  if (Date.now() - last < 10_000) return;
  sessionStorage.setItem(KEY, String(Date.now()));
  event.preventDefault();
  window.location.reload();
});

const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onSuccess: () => {
      void queryClient.invalidateQueries();
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // Disable retry backoff under e2e (--mode test) so error-state assertions
      // are deterministic instead of racing the exponential retry delay.
      retry: import.meta.env.MODE === 'test' ? false : shouldRetry,
      retryDelay: calcRetryDelay,
    },
    mutations: { retry: 0 },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

setUnauthorizedHandler((redirectPath) => {
  void queryClient.cancelQueries();
  queryClient.clear();
  void router.navigate({ to: '/login', search: { redirectPath } });
});

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('root element missing');

window.addEventListener('unhandledrejection', (e) => {
  if (e.reason instanceof Error) reportError(e.reason);
});

createRoot(rootEl).render(
  <StrictMode>
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </AppErrorBoundary>
  </StrictMode>,
);
